import type { ReapproCategory } from '@gozoki/api-types'
import { includesLowerCase } from '@gozoki/tools'
import { Check, Eye, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { Button } from '~/components/ui/button'

const formatHour = (value: string) => value.split(':').slice(0, 2).join(':')
const formatCoverageRate = (value: number) => Number.parseFloat(Number(value).toFixed(2)).toString()

interface ReapproCategoriesTableProps {
  categories: ReapproCategory[]
  groupNameById: Map<number, string>
  isLoading: boolean
  selectedIds: number[]
  onSelectionChange: (ids: number[]) => void
  isUserAdmin: boolean
  isDeleting: boolean
  onAdd: () => void
  onEdit: () => void
  onDelete: () => void
}

export const ReapproCategoriesTable = ({
  categories,
  groupNameById,
  isLoading,
  selectedIds,
  onSelectionChange,
  isUserAdmin,
  isDeleting,
  onAdd,
  onEdit,
  onDelete,
}: ReapproCategoriesTableProps) => {
  const [search, setSearch] = useState('')

  const filteredCategories = categories.filter((category) =>
    includesLowerCase(category.name, search),
  )

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCategories.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(filteredCategories.map((c) => c.id))
    }
  }

  const toggleSelectCategory = (id: number) => {
    onSelectionChange(
      selectedIds.includes(id) ? selectedIds.filter((c) => c !== id) : [...selectedIds, id],
    )
  }

  const allSelected =
    filteredCategories.length > 0 && selectedIds.length === filteredCategories.length

  const deleteDisabled = selectedIds.length === 0 || isDeleting
  const modifyDisabled = selectedIds.length !== 1 || isDeleting

  const tableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">
            Chargement...
          </td>
        </tr>
      )
    }
    if (filteredCategories.length === 0) {
      return (
        <tr>
          <td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">
            Aucune catégorie trouvée
          </td>
        </tr>
      )
    }
    return filteredCategories.map((category) => {
      const isSelected = selectedIds.includes(category.id)
      return (
        <tr
          key={category.id}
          className={`border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/30 ${isSelected ? 'bg-primary/5' : ''}`}
          onClick={() => toggleSelectCategory(category.id)}
        >
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelectCategory(category.id)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded accent-primary"
            />
          </td>
          <td className="px-4 py-3 text-sm">{category.name}</td>
          <td className="px-4 py-3 text-sm">
            {category.reapproCategoryGroupId != null
              ? (groupNameById.get(category.reapproCategoryGroupId) ?? '—')
              : '—'}
          </td>
          <td className="px-4 py-3 text-sm">{category.daysBefore}</td>
          <td className="px-4 py-3 text-sm">{formatCoverageRate(category.coverageRate)}</td>
          <td className="px-4 py-3 text-sm">{formatHour(category.computeHour)}</td>
          <td className="px-4 py-3 text-sm">{formatHour(category.sendHour)}</td>
          <td className="px-4 py-3 text-sm">
            {category.dispatchComputeHour ? formatHour(category.dispatchComputeHour) : '—'}
          </td>
          <td className="px-4 py-3 text-sm">
            {category.dispatchSendHour ? formatHour(category.dispatchSendHour) : '—'}
          </td>
          <td className="px-4 py-3 text-sm">
            {category.isDispatch ? (
              <Check size={16} className="text-green-600" />
            ) : (
              <X size={16} className="text-red-500" />
            )}
          </td>
        </tr>
      )
    })
  }

  return (
    <div className="mx-2 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center border border-border rounded-lg px-3 py-2 w-64 bg-background">
          <Search size={16} className="text-muted-foreground mr-2 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Recherche"
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          {isUserAdmin && (
            <Button variant="destructive" onClick={onDelete} disabled={deleteDisabled}>
              <Trash2 />
              Supprimer
            </Button>
          )}
          <Button variant="outline" disabled={modifyDisabled} onClick={onEdit}>
            {isUserAdmin ? (
              <>
                <Pencil />
                Modifier
              </>
            ) : (
              <>
                <Eye />
                Voir
              </>
            )}
          </Button>
          <Button onClick={onAdd}>
            <Plus />
            Ajouter
          </Button>
        </div>
      </div>

      <div className="bg-background rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded accent-primary"
                />
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Nom
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Groupe
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                J-X
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Taux couverture
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Heure de calcul
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Heure d'envoi
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                H. calcul dispatch
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                H. envoi dispatch
              </th>
              <th className="text-left px-4 py-3 text-xs text-muted-foreground uppercase tracking-wider">
                Dispatch
              </th>
            </tr>
          </thead>
          <tbody>{tableBody()}</tbody>
        </table>
      </div>
    </div>
  )
}
